import { apiUrl } from '../auth/client';

/**
 * Единственный способ обратиться к API.
 *
 * ЗАЧЕМ СЛОЙ, А НЕ ПРАВКА ПО МЕСТУ. К блоку 6 в проекте накопилось ЧЕТЫРЕ
 * случая, когда операция не срабатывала и при этом не давала ошибки:
 *
 *   1. форма сообщений вендора не совпала ни в одном поле - ноль событий из
 *      255 сообщений, без единой записи в журнале;
 *   2. подарок с флагом `combo`, но не серийный, ждал завершения серии,
 *      которого не бывает, - и терялся;
 *   3. идентификатор подарка в привязке и в событии оказались из разных
 *      пространств имён - привязка не срабатывала ни разу;
 *   4. POST без тела с заголовком `application/json` отклонялся Fastify кодом
 *      400 - кнопка нажималась, ничего не происходило, ошибка была видна
 *      только в консоли браузера.
 *
 * Общее у всех четырёх: ОТСУТСТВИЕ РЕЗУЛЬТАТА ВЫГЛЯДИТ КАК ОТСУТСТВИЕ
 * СОБЫТИЯ. Пользователь не может отличить «не сработало» от «нечего было
 * делать», а разработчик - от «никто не нажимал».
 *
 * Поэтому здесь два механических правила, а не дисциплина вызывающего:
 *
 *   - заголовок `Content-Type` ставится ТОЛЬКО вместе с телом. Запрос без
 *     тела формируется корректно ПО ПОСТРОЕНИЮ, а не по внимательности;
 *   - неуспешный ответ становится `ApiError` с текстом от сервера, и этот
 *     текст доходит до пользователя (см. `errors.ts`).
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly path: string,
    message: string,
    /** Машинный код от сервера, если он его прислал. */
    readonly code?: string,
    /** Разобранное тело отказа: экрану бывает нужен не текст, а поля. */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Человеческий текст по коду ответа: «400» пользователю не говорит ничего. */
function explainStatus(status: number): string {
  switch (status) {
    case 400:
      return 'Сервер не принял запрос.';
    case 401:
      return 'Сессия истекла. Войдите заново.';
    case 403:
      return 'Недостаточно прав для этого действия.';
    case 404:
      return 'Не найдено — возможно, объект уже удалён.';
    case 409:
      return 'Действие сейчас невозможно.';
    case 429:
      return 'Слишком часто. Подождите немного.';
    default:
      return status >= 500 ? 'Сервер не отвечает.' : 'Запрос не выполнен.';
  }
}

async function readError(response: Response, path: string): Promise<ApiError> {
  let code: string | undefined;
  let message: string | undefined;
  let parsed: unknown;

  try {
    const body = (await response.json()) as {
      error?: unknown;
      message?: unknown;
      action?: unknown;
    };

    parsed = body;
    code = typeof body.error === 'string' ? body.error : undefined;

    // Текст сервера предпочтительнее нашего: у отказов подключения к эфиру
    // он уже написан по-человечески и содержит действие.
    const parts = [body.message, body.action].filter(
      (part): part is string => typeof part === 'string' && part !== '',
    );

    message = parts.length > 0 ? parts.join(' ') : undefined;
  } catch {
    message = undefined;
  }

  return new ApiError(
    response.status,
    path,
    message ?? explainStatus(response.status),
    code,
    parsed,
  );
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** Тело запроса. Отсутствует - значит и заголовка типа не будет. */
  body?: unknown;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';

  const response = await fetch(apiUrl(path), {
    method,
    credentials: 'include',
    // Заголовок только вместе с телом - см. случай 4 выше.
    ...(options.body === undefined
      ? {}
      : {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(options.body),
        }),
  });

  if (!response.ok) {
    throw await readError(response, path);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Загрузка файла: тело запроса - сам файл.
 *
 * Без multipart: разбор его на сервере стоил бы зависимости в образ ради
 * одного маршрута, а имя файла прекрасно помещается в запрос.
 */
export async function upload<T>(path: string, file: File): Promise<T> {
  const response = await fetch(apiUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': file.type === '' ? 'application/octet-stream' : file.type },
    body: file,
  });

  if (!response.ok) {
    throw await readError(response, path);
  }

  return (await response.json()) as T;
}
